import React, { useState } from 'react';
import {
  SearchOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { Input } from 'components/FormFields';
import Table from 'components/Table';
import Pagination from 'components/Pagination';
import { PAGINATION, STATUS_VALUES } from 'helper/const';
import { applySearch } from 'utils';
import Spin from 'components/Spin';
import { useActions, useIndexData } from './selectorData';
import { orderColumns } from './utils';
import { DEFAULT_SORT, SORT_NAME_MAPPING } from './constants';
import classes from './UserManagementTab.module.scss';
import toast from 'helper/toast';
import { confirmModalBlueConfig, getConfirmModal } from 'utils/modal';
import { useModal } from 'components/Modal';

const PasswordConfirmContent = ({ password, isResentInvite = false }) => {
  const [visible, setVisible] = useState(false);
  const maskedPassword = '•'.repeat(password.length || 8);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied to clipboard');
  };

  return (
    <div>
      <p>
        {isResentInvite
          ? 'Invitation has been sent successfully. Temporary password:'
          : 'The password has been reset successfully. The new password is:'}
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          background: '#f5f7fa',
          borderRadius: 6,
          marginTop: 12,
        }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: 14 }}>
          {visible ? password : maskedPassword}
        </span>
        {visible ? (
          <EyeInvisibleOutlined
            onClick={() => setVisible(false)}
            style={{ cursor: 'pointer', color: '#555' }}
            title="Hide password"
          />
        ) : (
          <EyeOutlined
            onClick={() => setVisible(true)}
            style={{ cursor: 'pointer', color: '#555' }}
            title="Show password"
          />
        )}

        <CopyOutlined
          onClick={handleCopy}
          style={{ cursor: 'pointer', color: '#1677ff' }}
          title="Copy password"
        />
      </div>

      <p style={{ marginTop: 10, color: '#888' }}>
        Please share this password securely with the user.
      </p>
    </div>
  );
};
const UserManagementTab = ({ clientId, onUsersFilterChanged }) => {
  const [modal, contextHolder] = useModal();
  const confirmModal = getConfirmModal(modal, confirmModalBlueConfig);

  // eslint-disable-next-line
  const [checkedRows, setCheckedRows] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { users, total, usersLoading } = useIndexData();
  const {
    getCompanyUsers,
    resendInviteCompanyTeamMember,
    resetCompanyTeamMemberPassword,
    updateCompanyTeamMemberIndividualStatus,
  } = useActions();
  const handleFilterChanged = (key, value) => {
    if (key === 'search') {
      setSearch(value);
    } else {
      setCurrentPage(value);
    }
  };
  const reloadData = (val, page, tSort) => {
    const tFilters = {
      page,
      search: val,
    };
    if (!!tSort?.order) {
      tFilters.sort_by = SORT_NAME_MAPPING[tSort.field] || tSort.field;
      tFilters.order = tSort.order === 'ascend' ? 'asc' : 'desc';
    }
    getCompanyUsers(clientId, tFilters);
    if (onUsersFilterChanged) {
      onUsersFilterChanged(tFilters);
    }
  };
  const handleSearch = val => {
    handleFilterChanged('page', 1);
    reloadData(val, 1, sort);
  };
  const handleResendInvite = (records, cb) => {
    const data = {
      id: records[0]?.id,
      is_internal_user_inviting: true,
    };

    resendInviteCompanyTeamMember(clientId, data, res => {
      if (res) {
        confirmModal(
          'Invite Successful',
          <PasswordConfirmContent password={res.password} />,
          () => {
            if (typeof cb === 'function') cb();
          }
        );
      } else {
        toast.error('Teammate resend invite failed!');
      }
    });
  };

  const handleApproveUser = (records, cb) => {
    updateCompanyTeamMemberIndividualStatus(
      clientId,
      records[0]?.id,
      'approve',
      res => {
        reloadData('', 1, sort);
        if (res) {
          toast.success('Teammate approved successfully!');
        } else {
          toast.error('Unable to approve teammate request!');
        }
      }
    );
  };

  const handleResetPassword = (records, cb) => {
    const data = {
      id: records[0]?.id,
      is_internal_invite: true,
    };

    resetCompanyTeamMemberPassword(clientId, data, res => {
      if (res) {
        confirmModal(
          'Password Reset Successful',
          <PasswordConfirmContent password={res.password} />,
          () => {
            if (typeof cb === 'function') cb();
          }
        );
      } else {
        toast.error('Teammate password reset failed!');
      }
    });
  };
  const handleAction = record => {
    const { status } = record;
    const fnMapping = {
      [STATUS_VALUES.ACTIVE]: handleResetPassword,
      [STATUS_VALUES.INVITE_SENT]: handleResendInvite,
      [STATUS_VALUES.PENDING_APPROVAL]: handleApproveUser,
    };
    if (fnMapping[status]) {
      fnMapping[status]([record], () => {
        reloadData('', 1, sort);
      });
    }
  };
  const columns = orderColumns(handleAction);

  return (
    <>
      <div className={classes.userManagementTab}>
        <div className={classes.tabContentHeader}>
          <Input
            size={'medium'}
            name="keyword"
            value={search}
            placeholder={`Search users`}
            allowClear
            onChange={e => {
              const value = e.target.value;
              handleFilterChanged('search', value);
              if (!value) {
                handleSearch('');
              } else {
                applySearch(value, val => {
                  handleSearch(val);
                });
              }
            }}
            prefix={<SearchOutlined />}
            isBlue
          />
        </div>
        <Spin spinning={!!usersLoading} icon tip={'Loading...'}>
          <Table
            rowSelection={{
              onChange: selectedRowKeys => {
                setCheckedRows(selectedRowKeys);
              },
            }}
            columns={columns}
            data={users}
            rowKey="id"
            loading={false}
            isScroll
            scroll={{ x: 850 }}
            showSorterTooltip={false}
            onSortChange={sorter => {
              const newSort = {
                field: sorter.field,
                order: sorter.order,
              };
              setSort(newSort);
              reloadData(search, currentPage, newSort);
            }}
            // rowIsClick
            // onRow={() => {
            //   return {
            //     onClick: event => {
            //       // row click
            //     },
            //   };
            // }}
          />
        </Spin>

        {total > 0 && (
          <Pagination
            current={currentPage}
            pageSize={PAGINATION.PER_PAGE}
            total={total}
            right
            onChange={page => {
              handleFilterChanged('page', page);
              reloadData(search, page, sort);
            }}
            className={classes.pagination}
          />
        )}
      </div>
      {contextHolder}
    </>
  );
};

export default UserManagementTab;
